import React, { FC } from 'react';
import { T } from 'src/shared/helpers';
import styled from 'styled-components';
import { BasicButton } from '../../../../widgets/buttons';
import { Dialog, DialogWindow } from '../../../../widgets/dialog';
import { LocalModel3DItem } from '../../assetLibrary/assetItem';

const InfoWindow = styled(DialogWindow)`
    width: 800px;
    min-height: 378px;
    max-height: calc(100vh - 40px);
    max-width: calc(100vw - 40px);
    padding: 28px 32px 24px;
    box-sizing: border-box;
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding-top: 8px;
`;

const Section = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;

    &:not(:last-child) {
        padding-bottom: 20px;
        border-bottom: 1px solid #e5e7eb;
    }
`;

const SectionTitle = styled.div`
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #78909c;
`;

const InfoGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
`;

const InfoCard = styled.div<{ $fullWidth?: boolean }>`
    background: #f6f8f9;
    border-radius: 10px;
    padding: 12px 14px;
    grid-column: ${(props) => (props.$fullWidth ? '1 / -1' : 'auto')};
`;

const InfoLabel = styled.div`
    font-size: 12px;
    font-weight: 500;
    color: #78909c;
    margin-bottom: 4px;
`;

const InfoValue = styled.div`
    font-size: 14px;
    color: ${(props) => props.theme.app.textColor};
    word-break: break-word;
    line-height: 1.4;
`;

const FooterRow = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-top: 8px;
`;

const TOOL_DISPLAY_NAMES: Record<string, string> = {
    'text-to-3d': 'Text to 3D',
    'image-to-3d': 'Image to 3D',
    'multi-views-to-3d': 'Multi-View to 3D'
};

interface Local3DInfoDialogProps {
    item: LocalModel3DItem;
    onClose: () => void;
}

export const Local3DInfoDialog: FC<Local3DInfoDialogProps> = ({ item, onClose }) => {
    const toolLabel = TOOL_DISPLAY_NAMES[item.toolId] || '3D Model';
    const savedAt = new Date(item.createdAt).toLocaleString();

    return (
        <Dialog title={`${T._('Info', 'Admin')} - ${item.label}`} onClose={onClose} windowDecorator={InfoWindow}>
            <Content>
                <Section>
                    <SectionTitle>{T._('General', 'Admin')}</SectionTitle>
                    <InfoGrid>
                        <InfoCard>
                            <InfoLabel>{T._('Tool', 'Admin')}</InfoLabel>
                            <InfoValue>{toolLabel}</InfoValue>
                        </InfoCard>
                        <InfoCard>
                            <InfoLabel>{T._('Saved At', 'Admin')}</InfoLabel>
                            <InfoValue>{savedAt}</InfoValue>
                        </InfoCard>
                    </InfoGrid>
                </Section>

                {item.prompt && (
                    <Section>
                        <SectionTitle>{T._('Prompts', 'Admin')}</SectionTitle>
                        <InfoCard $fullWidth>
                            <InfoLabel>{T._('Prompt', 'Admin')}</InfoLabel>
                            <InfoValue>{item.prompt}</InfoValue>
                        </InfoCard>
                    </Section>
                )}

                <FooterRow>
                    <BasicButton onClick={onClose}>{T._('Close', 'Admin')}</BasicButton>
                </FooterRow>
            </Content>
        </Dialog>
    );
};

export default Local3DInfoDialog;
